import { HrbpScreenReview } from "@/views/HrbpApplication";
import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { ObjectReadGuard } from "@/ui/ObjectReadGuard";
import { NotFound } from "@/views/NotFound";
import { B3RoundCards } from "@/views/screen/RoundCards";
import { useEffect, useRef, useState } from "react";
import {
  candidateOpen,
  roundCorrectHeldAt,
  roundMarkHeld,
  roundMarkNotHeld,
  roundOpen,
  screenOpenResult,
  screenSaveForm,
  screenSubmitResult,
  type RoundOpenOutput,
  type ScreenSaveFormInput,
} from "@/api/tools";
import { businessLocalToISO, isoToBusinessLocal } from "@/api/runtime";
import { isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { Banner, Dialog, Empty, Field, KV, Pill, ReasonDialog, Skeleton, fmtDate, fmtMoney } from "@/ui";
import { McPage } from "@/shell/page";
import { RESPONSE_PILL, SCHEDULING_LABELS } from "@/mc";
import { ScheduleSection, ScheduleStandalone } from "@/views/scheduling/ScheduleSection";
import type { ScheduleSubject } from "@/views/scheduling/schedule";
import { ROLE_WORD, responseStatus } from "@/views/scheduling/workbench";

// V1-INTERACTIONS §4.7 (Recruiter screen), §4.9 (HM round), §4.13 (scheduling). One round route per candidate:
// `#requisitions/<R>/candidates/<cand>/rounds/<n>` — 1 = the Recruiter Screen, 2 = the Hiring Manager round, rd_… one
// Final Interview Loop round. The scheduling card is B5's (scheduling/ScheduleSection, a design-system section above);
// the occurrence (held, not held, the held time), the scorecard card and the screen sections below stay V1 (B7, B3).
// A seat that schedules but does not read the round (the Coordinator) gets the scheduling card alone.

const KIND_FOR_N: Record<string, "recruiter_screen" | "hm_round"> = { "1": "recruiter_screen", "2": "hm_round" };
const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { planned: "muted", scheduled: "info", rescheduled: "warn", held: "ok", not_held: "bad", cancelled: "muted" };
// "Not booked / Booked / Moved — rebook" left the page with S12 (G §22): the scheduling card shows the eight statuses.
const STATUS_LABEL: Record<string, string> = { held: "Held", not_held: "Not held", cancelled: "Cancelled" };
const NOT_HELD: [string, string][] = [
  ["candidate_no_show", "Candidate did not attend"],
  ["interviewer_no_show", "Interviewer did not attend"],
  ["cancelled_by_candidate", "Cancelled by the candidate"],
  ["cancelled_by_recruiter", "Cancelled by the Recruiter"],
  ["other", "Other"],
];

export function RoundPage({ rid, aid, n }: { rid: string; aid: string; n: string }) {
  const { register } = useViewState();
  const { me } = useSession();
  const onsiteId = n.startsWith("rd_") ? n : null;
  const known = onsiteId !== null || KIND_FOR_N[n] !== undefined;
  const kind: "recruiter_screen" | "hm_round" | "onsite" = onsiteId ? "onsite" : (KIND_FOR_N[n] ?? "recruiter_screen");
  const ref: { round_kind: "recruiter_screen" | "hm_round" | "onsite"; round_def_id?: string } = onsiteId ? { round_kind: "onsite", round_def_id: onsiteId } : { round_kind: kind };
  const q = useRead(["round", rid, aid, n], () => roundOpen({ requisition_id: rid, application_id: aid, ...ref }), { enabled: known });
  const app = useRead(["application", aid, rid], () => candidateOpen({ requisition_id: rid, application_id: aid }), { enabled: known });
  useEffect(() => register({ view: "round", ids: { requisition: rid, application: aid, round: n }, summary: `round ${n} ${q.data?.status ?? ""}` }), [register, rid, aid, n, q.data?.status]);
  const inval = [["round", rid, aid, n], ["application", aid], ["candidates", rid], ["screen", aid], ["today"], ["packets"]] as const;
  const held = useWrite((i: Parameters<typeof roundMarkHeld>[0], k) => roundMarkHeld(i, k), inval);
  const notHeld = useWrite((i: Parameters<typeof roundMarkNotHeld>[0], k) => roundMarkNotHeld(i, k), inval);
  const correct = useWrite((i: Parameters<typeof roundCorrectHeldAt>[0], k) => roundCorrectHeldAt(i, k), inval);
  const [dlg, setDlg] = useState<null | "not_held" | "correct" | "held">(null);
  const [nhReason, setNhReason] = useState<string>("candidate_no_show");
  const [heldAt, setHeldAt] = useState("");
  if (!known) return <NotFound route={`requisitions/${rid}/candidates/${aid}/rounds/${n}`} />;
  const subject: ScheduleSubject = { kind: "round", rid, aid, roundKind: kind, roundDefId: onsiteId ?? undefined };
  if (q.error && isClientRefusal(q.error)) return <ScheduleStandalone subject={subject} route={`requisitions/${rid}/candidates/${aid}/rounds/${n}`} />;
  if (q.error || !q.data) return <ObjectReadGuard query={q} />;
  const r = q.data;
  const isRecruiter = !!me?.roles.includes("recruiter") || !!me?.roles.includes("admin");
  const isHm = !!me?.roles.includes("hm");
  const err = held.error ?? notHeld.error ?? correct.error;
  const b = r.booking;
  const primary = String(r.primary["name"] ?? "");
  // A `datetime-local` control carries no zone: the wall clock typed here is read in the BUSINESS zone — the one every
  // timestamp on this page is printed in — never the reader's own (finding 25). Empty means "now" when marking held;
  // a half-typed value is not a time at all, so the action waits rather than sending something else.
  const heldAtISO = businessLocalToISO(heldAt);
  const heldAtBad = heldAt !== "" && heldAtISO === "";
  return (
    <div><RecordAfterFact id={rid} family="requisition" section="rounds" references={{ application_id: aid }} />
      <div className="hrow">
        <div>
          <h1 className="page">
            {r.round_label} · {r.candidate}
          </h1>
          <p className="lede">
            <a href={`#requisitions/${rid}/candidates/${aid}`}>← application</a> · {r.format} · {r.duration_minutes} min · with {primary}
            {r.backup && <span className="muted"> (backup {String(r.backup["name"])})</span>} · touchpoints {r.touchpoints["used"]} of {r.touchpoints["maximum"]}
          </p>
        </div>
        <div className="acts">
          {STATUS_LABEL[r.status] ? <Pill tone={STATUS_TONE[r.status] ?? "muted"}>{STATUS_LABEL[r.status]}</Pill> : null}
          {r.can["mark_held"] && (
            <button className="btn btn--primary" onClick={() => { setHeldAt(""); setDlg("held"); }}>
              Mark held
            </button>
          )}
          {r.can["mark_not_held"] && (
            <button className="btn" onClick={() => setDlg("not_held")}>
              Not held
            </button>
          )}
          {r.can["correct_held_at"] && (
            <button className="btn" onClick={() => { setHeldAt(isoToBusinessLocal(r.held_at)); setDlg("correct"); }}>
              Correct the held time
            </button>
          )}
          {r.packet_id && r.can["open_packet"] && (
            <a className="btn btn--primary" href={`#packets/${r.packet_id}/${aid}`}>
              Open my packet
            </a>
          )}
        </div>
      </div>
      <Banner error={err} />
      {kind === "recruiter_screen" && <B3RoundCards rid={rid} aid={aid} />}
      <McPage>
        <ScheduleSection
          subject={subject}
          held={r.status === "held" && r.held_at ? { at: r.held_at, offline: false, reason: null } : null}
          heldOffline={{
            // F-158 (DC-237): "Held offline — continue" — the seats that mark a round held (its Recruiter, its primary
            // interviewer), on a round not yet held whether or not it was ever booked in the app (the server decides)
            can: ["planned", "scheduled", "rescheduled"].includes(r.status)
              && (!!r.can["mark_held"] || isRecruiter || String(r.primary["person_id"] ?? "") === me?.person_id),
            attendees: [r.primary, r.backup]
              .filter((p): p is Record<string, unknown> => !!p && !!p["person_id"])
              .map((p, index) => ({ person_id: String(p["person_id"]), name: String(p["name"] ?? p["person_id"]), seat: "interviewer", required: index === 0 })),
            tool: "round.mark_held",
            record: (input) => held.mutateAsync({
              requisition_id: rid, application_id: aid, ...ref, held_at: input.held_at,
              attendance: Object.entries(input.present).map(([person_id, present]) => ({ person_id, present })),
              held_offline: true, offline_reason: input.offline_reason,
            }),
          }}
        />
      </McPage>
      <div className="grid grid--2">
        <section className="card">
          <h2 className="sec">Scorecard</h2>
          {r.packet_id ? (
            <KV items={[["Packet", <a href={`#packets/${r.packet_id}${r.can["open_packet"] ? `/${aid}` : ""}`}>{r.packet_id}</a>], ["Scorecard", r.scorecard_status ? <Pill tone={r.scorecard_status === "submitted" ? "ok" : r.scorecard_status === "due" ? "warn" : "muted"}>{r.scorecard_status.replace(/_/g, " ")}</Pill> : "—"]]} />
          ) : r.scorecard_status === null ? (
            <p className="muted">Scorecard details are sealed for this view.</p>
          ) : (
            <Empty heading="No scorecard to complete">{r.status === "planned" || r.status === "rescheduled" ? "The packet is generated and issued when the round is booked." : "Notes-only round — no rating rows."}</Empty>
          )}
          {r.held_at && r.scorecard_status === "due" && (
            <p className="muted">Held {fmtDate(r.held_at, true)} · the 24-hour scorecard clock runs for {primary}.</p>
          )}
        </section>
      </div>
      {kind === "recruiter_screen" && app.data?.projection === "hrbp" && <HrbpScreenReview rid={rid} aid={aid} />}
      {kind === "recruiter_screen" && (isRecruiter || isHm) && <ScreenSection rid={rid} aid={aid} roundStatus={r.status} scorecardStatus={r.scorecard_status} isRecruiter={isRecruiter && (app.data?.card.can["log_message"] ?? false)} attendees={attendeeWords(r)} />}

      <Dialog title="Mark held" open={dlg === "held"} onClose={() => setDlg(null)}>
        <Field label="Held at" id="rd-held" hint={heldAtISO ? `Recorded as ${fmtDate(heldAtISO, true)}` : "Defaults to now; type the time if it happened earlier"} error={heldAtBad ? "Give the date and the time it was held." : undefined}>
          <input id="rd-held" type="datetime-local" value={heldAt} onChange={(e) => setHeldAt(e.target.value)} />
        </Field>
        <div className="acts">
          <button className="btn" onClick={() => setDlg(null)}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={held.isPending || heldAtBad} onClick={() => void held.mutateAsync({ requisition_id: rid, application_id: aid, ...ref, held_at: heldAtISO || null }).then(() => setDlg(null))}>
            Mark held
          </button>
        </div>
      </Dialog>
      <ReasonDialog title="Not held" label="Note (optional)" optional confirm="Record" open={dlg === "not_held"} onClose={() => setDlg(null)} busy={notHeld.isPending} error={notHeld.error} onConfirm={(note) => void notHeld.mutateAsync({ requisition_id: rid, application_id: aid, ...ref, reason: nhReason as "other", note: note || null }).then(() => setDlg(null))}>
        <Field label="Reason" required id="rd-nh">
          <select id="rd-nh" value={nhReason} onChange={(e) => setNhReason(e.target.value)}>
            {NOT_HELD.map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Correct the held time" label="Why" confirm="Correct" open={dlg === "correct"} onClose={() => setDlg(null)} busy={correct.isPending} error={correct.error} onConfirm={(reason) => { if (!heldAtISO) return; void correct.mutateAsync({ requisition_id: rid, application_id: aid, ...ref, held_at: heldAtISO, reason }).then(() => setDlg(null)); }}>
        <Field label="Held at" required id="rd-corr" hint={heldAtISO ? `Corrected to ${fmtDate(heldAtISO, true)}` : undefined} error={heldAtISO ? undefined : "Give the date and the time it was held."}>
          <input id="rd-corr" type="datetime-local" value={heldAt} onChange={(e) => setHeldAt(e.target.value)} />
        </Field>
      </ReasonDialog>
    </div>
  );
}

// ---- the screen form (Recruiter) and the screen results (Hiring Manager) --------------------------------------------------------

type FormState = { motivation: string; comp_expectation: string; notice_period: string; earliest_start: string; location: string; mobility: string; work_authorisation: string; work_authorisation_problem: boolean; must_have_verification: { requirement: string; verified: "yes" | "no" | "partial" | ""; note: string }[]; notes: string; summary: string };

// The Hiring Manager's Step 9 decision is the HM interview selection card above (B3RoundCards): Step 9 reuses Step 7's
// control, Advance · Hold · Pass (A71, DC-106; BRD §3.7 "Retain Advance, Hold, and Pass."), so this section only shows
// the Hiring Manager the submitted results and carries no decision of its own.
function ScreenSection({ rid, aid, roundStatus, scorecardStatus, isRecruiter, attendees }: { rid: string; aid: string; roundStatus: string; scorecardStatus: string | null; isRecruiter: boolean; attendees: readonly string[] }) {
  const q = useRead(["screen", aid], () => screenOpenResult({ requisition_id: rid, application_id: aid }), { retry: false });
  const inval = [["screen", aid], ["round", rid, aid, "recruiter_screen"], ["application", aid], ["candidates", rid], ["today"]] as const;
  const save = useWrite((i: ScreenSaveFormInput, k) => screenSaveForm(i, k), inval);
  const submit = useWrite((i: { requisition_id: string; application_id: string; summary: string }, k) => screenSubmitResult(i, k), inval);
  const [f, setF] = useState<FormState | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const [dlg, setDlg] = useState<null | "submit">(null);
  const timer = useRef<number | null>(null);
  const data = q.data;
  useEffect(() => {
    if (!data || f) return;
    const form = data.form;
    const mh = (form["must_have_verification"] as FormState["must_have_verification"] | null) ?? [];
    setF({
      motivation: String(form["motivation"] ?? ""),
      comp_expectation: form["comp_expectation"] != null ? String(form["comp_expectation"]) : "",
      notice_period: String(form["notice_period"] ?? ""),
      earliest_start: String(form["earliest_start"] ?? ""),
      location: String(form["location"] ?? ""),
      mobility: String(form["mobility"] ?? ""),
      work_authorisation: String(form["work_authorisation"] ?? ""),
      work_authorisation_problem: false,
      must_have_verification: data.must_haves.map((m) => mh.find((x) => x.requirement === m) ?? { requirement: m, verified: "", note: "" }),
      notes: String(form["notes"] ?? ""),
      summary: String(form["summary"] ?? ""),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  if (q.isError && !isRecruiter) return null; // the results are not submitted yet: nothing for the Hiring Manager
  if (q.isLoading || !data) return <Skeleton rows={3} />;
  const editable = isRecruiter && !!data.can["save_form"];
  const flush = (state: FormState) => {
    setStatus("saving");
    void save
      .mutateAsync({ requisition_id: rid, application_id: aid, motivation: state.motivation || null, comp_expectation: state.comp_expectation ? Number(state.comp_expectation) : null, notice_period: state.notice_period || null, earliest_start: state.earliest_start || null, location: state.location || null, mobility: state.mobility || null, work_authorisation: state.work_authorisation || null, work_authorisation_problem: state.work_authorisation_problem, must_have_verification: state.must_have_verification.filter((x) => x.verified), notes: state.notes || null, summary: state.summary || null })
      .then(() => setStatus("saved"))
      .catch(() => setStatus("failed"));
  };
  const change = (patch: Partial<FormState>) => {
    if (!f) return;
    const next = { ...f, ...patch };
    setF(next);
    if (!editable) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => flush(next), 2000);
  };
  const above = f && f.comp_expectation && data.comp_range?.["max"] != null && Number(f.comp_expectation) > Number(data.comp_range?.["max"]);
  return (
    <section className="card" style={{ marginTop: 14 }}>
      <div className="hrow">
        <h2 className="sec">{editable ? "Screen form" : "Screen results"}</h2>
        <div className="acts">
          {editable && <span className="muted" role="status" aria-live="polite">{status === "saving" ? "Saving…" : status === "saved" ? "Saved" : status === "failed" ? "Not saved — retrying" : ""}</span>}
          {data.submitted_at && <Pill tone="ok">results submitted {fmtDate(data.submitted_at)}</Pill>}
          {data.above_range && <Pill tone="warn">above the approved range</Pill>}
          {isRecruiter && data.can["submit_result"] && (
            <button className="btn btn--primary" onClick={() => setDlg("submit")}>
              Submit results to the Hiring Manager
            </button>
          )}
        </div>
      </div>
      {isRecruiter && !data.submitted_at && (
        <p className="muted">
          {roundStatus !== "held" ? "Mark the screen held to submit results. " : ""}
          {!data.notes_only && scorecardStatus !== "submitted" ? "Submit your screen scorecard first (Open my packet). " : ""}
          {data.missing.length > 0 ? `Still needed: ${data.missing.join(" · ")}` : "Form complete."}
        </p>
      )}
      <Banner error={save.error ?? submit.error} />
      {/* the expectation is stated in the currency it was RECORDED in (the record carries its own), never in the
          requisition's approved-range currency — the approved range only says what the figure is compared against */}
      {f && !editable && <ScreenRead f={f} holder={data.projection === "holder"} currency={String(data.form["comp_expectation_currency"] ?? data.comp_range?.["currency"] ?? "USD")} aboveRange={data.above_range} attendees={attendees} />}
      {f && editable && (
        <div className="grid grid--2">
          <div>
            <Field label="Motivation" required id="sf-mot">
              <textarea id="sf-mot" rows={3} value={f.motivation} onChange={(e) => change({ motivation: e.target.value })} disabled={!editable} />
            </Field>
            <Field label="Compensation expectation" required id="sf-comp" hint={`Approved range ${fmtMoney(String(data.comp_range?.["min"] ?? ""), String(data.comp_range?.["currency"] ?? "USD"))} – ${fmtMoney(String(data.comp_range?.["max"] ?? ""), String(data.comp_range?.["currency"] ?? "USD"))}${above ? " · above the approved range" : ""}`}>
              <input id="sf-comp" type="number" value={f.comp_expectation} onChange={(e) => change({ comp_expectation: e.target.value })} disabled={!editable} />
            </Field>
            <Field label="Notice period" required id="sf-notice">
              <input id="sf-notice" value={f.notice_period} onChange={(e) => change({ notice_period: e.target.value })} disabled={!editable} />
            </Field>
            <Field label="Earliest start" required id="sf-start">
              <input id="sf-start" type="date" value={f.earliest_start} onChange={(e) => change({ earliest_start: e.target.value })} disabled={!editable} />
            </Field>
            <Field label="Location & mobility" id="sf-loc">
              <input id="sf-loc" value={f.location} onChange={(e) => change({ location: e.target.value })} disabled={!editable} placeholder="City" />
              <textarea rows={2} aria-label="Mobility" value={f.mobility} onChange={(e) => change({ mobility: e.target.value })} disabled={!editable} placeholder="Relocation, travel, remote" />
            </Field>
          </div>
          <div>
            <Field label="Work authorisation" required id="sf-auth">
              <input id="sf-auth" value={f.work_authorisation} onChange={(e) => change({ work_authorisation: e.target.value })} disabled={!editable} />
            </Field>
            {editable && (
              <label className="check">
                <input type="checkbox" checked={f.work_authorisation_problem} onChange={(e) => change({ work_authorisation_problem: e.target.checked })} /> Raise a work authorisation problem to the HR Business Partner
              </label>
            )}
            <h3 className="sec">Must-have verification</h3>
            {f.must_have_verification.length === 0 && <p className="muted">No must-haves were listed in the intake.</p>}
            {f.must_have_verification.map((m, i) => (
              <div key={m.requirement} className="mh">
                <b>{m.requirement}</b>
                <select aria-label={`Verified: ${m.requirement}`} value={m.verified} onChange={(e) => change({ must_have_verification: f.must_have_verification.map((x, j) => (j === i ? { ...x, verified: e.target.value as "yes" } : x)) })} disabled={!editable}>
                  <option value="">—</option>
                  <option value="yes">Verified</option>
                  <option value="partial">Partial</option>
                  <option value="no">Not verified</option>
                </select>
                <input aria-label={`Note: ${m.requirement}`} value={m.note} onChange={(e) => change({ must_have_verification: f.must_have_verification.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)) })} disabled={!editable} placeholder="note" />
              </div>
            ))}
            {isRecruiter && (
              <Field label="Notes (yours)" id="sf-notes">
                <textarea id="sf-notes" rows={3} value={f.notes} onChange={(e) => change({ notes: e.target.value })} disabled={!editable} />
              </Field>
            )}
            <Field label="Summary for the Hiring Manager" required id="sf-sum" hint="What the Hiring Manager reads; ratings stay sealed until the reveal">
              <textarea id="sf-sum" rows={4} value={f.summary} onChange={(e) => change({ summary: e.target.value })} disabled={!editable} />
            </Field>
          </div>
        </div>
      )}
      <ReasonDialog title="Submit results to the Hiring Manager" label="Summary" confirm="Submit" open={dlg === "submit"} onClose={() => setDlg(null)} busy={submit.isPending} error={submit.error} onConfirm={(summary) => void submit.mutateAsync({ requisition_id: rid, application_id: aid, summary }).then(() => setDlg(null))}>
        <p className="muted">Sends the screen form and your summary. The Hiring Manager decides within three business days.</p>
      </ReasonDialog>
    </section>
  );
}

// ---- the screen results as a READER sees them (V1-INTERACTIONS §4.9) ------------------------------------------------------------
// A reader — the Hiring Manager deciding, or the Recruiter after the results went out — cannot write this form, and a form
// of disabled controls reads as grey text in dead boxes. The same answers are projected as a label · value list, prose kept
// as prose, so the Hiring Manager reads the screen rather than deciphering a locked form.

const VERIFIED_LABEL: Record<string, string> = { yes: "Verified", partial: "Partial", no: "Not verified", "": "Not recorded" };
// F-62: a field the Recruiter left empty says so in words; a bare dash reads as a value that failed to load
const NOT_PROVIDED = "Not provided";

type BookingAttendee = { person_id?: unknown; role?: unknown; response?: unknown };

/** F-62: the round's attendees as a reader sees them — each by name (the round's primary or backup), else by their role
 *  word, never a role key — with their response in his words (Jordan Lee · Accepted). A booking read without its
 *  attendees (a seat that sees only the time) draws none. */
export function attendeeWords(r: Pick<RoundOpenOutput, "booking" | "primary" | "backup">): string[] {
  const list = (r.booking?.["attendees"] as BookingAttendee[] | null | undefined) ?? [];
  const names = new Map<string, string>();
  for (const p of [r.primary, r.backup]) if (p) names.set(String(p["person_id"]), String(p["name"] ?? ""));
  return list.flatMap((a) => {
    const name = names.get(String(a.person_id)) || ROLE_WORD[String(a.role)];
    return name ? [`${name} · ${RESPONSE_PILL[responseStatus(String(a.response ?? ""))].word}`] : [];
  });
}

function Row({ label, value, prose = false }: { label: string; value: string; prose?: boolean }) {
  return (
    <div style={{ display: "contents" }}>
      <dt>{label}</dt>
      <dd className={prose ? "prose" : undefined}>{value.trim() || NOT_PROVIDED}</dd>
    </div>
  );
}

/** a plain calendar day (no time, no zone): formatted from its own parts so it is never shifted a day by a zone */
function fmtDay(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return v.trim();
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString(undefined, { dateStyle: "medium", timeZone: "UTC" });
}

export function ScreenRead({ f, holder, currency, aboveRange, attendees = [] }: { f: FormState; holder: boolean; currency: string; aboveRange: boolean; attendees?: readonly string[] }) {
  return (
    <>
      <dl className="kv kv--rows">
        {attendees.length > 0 && <Row label={SCHEDULING_LABELS.attendees} value={attendees.join(", ")} />}
        <Row label="Motivation" value={f.motivation} prose />
        <Row label="Compensation expectation" value={f.comp_expectation ? `${fmtMoney(f.comp_expectation, currency)}${aboveRange ? " · above the approved range" : ""}` : ""} />
        <Row label="Notice period" value={f.notice_period} />
        <Row label="Earliest start" value={fmtDay(f.earliest_start)} />
        <Row label="Location" value={f.location} />
        <Row label="Mobility" value={f.mobility} prose />
        <Row label="Work authorisation" value={f.work_authorisation} />
        {holder && <Row label="Notes (yours)" value={f.notes} prose />}
        <Row label="Summary for the Hiring Manager" value={f.summary} prose />
      </dl>
      <h3 className="sec">Must-have verification</h3>
      {f.must_have_verification.length === 0 ? (
        <p className="muted">No must-haves were listed in the intake.</p>
      ) : (
        f.must_have_verification.map((m) => (
          <div key={m.requirement} className="mh">
            <b>{m.requirement}</b>
            <span>{VERIFIED_LABEL[m.verified] ?? "Not recorded"}</span>
            <span className="muted">{m.note.trim() || NOT_PROVIDED}</span>
          </div>
        ))
      )}
      {!holder && <p className="muted">Ratings and the recommendation stay sealed until the debrief reveal.</p>}
    </>
  );
}
